import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

const SuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const { clearCart } = useCart();
  const cleared = useRef(false);

  const handleResult = useCallback(
    (res) => {
      setData(res);
      if (res.payment_status === "paid") {
        setStatus("paid");
        if (!cleared.current) {
          cleared.current = true;
          clearCart();
        }
        return true;
      }
      if (res.status === "expired") {
        setStatus("expired");
        return true;
      }
      return false;
    },
    [clearCart]
  );

  useEffect(() => {
    if (!sessionId) {
      setStatus("missing");
      return undefined;
    }
    let cancelled = false;
    let attempts = 0;
    let timer;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (cancelled) return;
        if (handleResult(res)) return;
        if (attempts >= 8) {
          setStatus("timeout");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        console.warn("[success] poll failed", err);
        if (!cancelled) setStatus("error");
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, handleResult]);

  return (
    <div className="container-x py-24 md:py-32" data-testid="success-page">
      <div className="max-w-xl">
        <p className="overline mb-4">Order Status</p>
        {status === "checking" && (
          <>
            <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tightest font-bold leading-[0.95]">
              Confirming
              <br />
              your order…
            </h1>
            <p className="text-mute mt-6 mono text-sm">Polling Stripe for payment confirmation.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tightest font-bold leading-[0.95]">
              Paid.
              <br />
              <span className="text-mute">Welcome to Pritch.</span>
            </h1>
            <p className="mt-6 text-mute">
              Your matchday rotation is on its way. You'll receive a confirmation email and tracking
              shortly.
            </p>
            {data && (
              <div className="mt-8 border border-line p-5 bg-white" data-testid="success-summary">
                <div className="flex justify-between mb-2">
                  <span className="overline">Total paid</span>
                  <span className="mono">
                    {formatPrice((data.amount_total || 0) / 100, data.currency || "GBP")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="overline">Reference</span>
                  <span className="mono text-xs truncate max-w-[60%]">{sessionId}</span>
                </div>
              </div>
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" className="btn-primary">
                Keep shopping
              </Link>
              <Link to="/" className="btn-secondary">
                Back to home
              </Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout" || status === "missing") && (
          <>
            <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tightest font-bold leading-[0.95]">
              Something's off.
            </h1>
            <p className="mt-6 text-mute">
              We couldn't confirm your payment automatically. If you've been charged you'll receive
              email confirmation. Otherwise, please retry checkout.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cart" className="btn-primary">
                Back to cart
              </Link>
              <Link to="/contact" className="btn-secondary">
                Contact us
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
